<template>
  <div>
    <LimitedAccessBanner
      v-show="limitedAccess || showAttestation"
      :rows="feedsRow"
      :attestationMessage="showAttestation"
    />
    <mds-empty-state
      class="saved-feed__response-message"
      :title="translate(title)"
      :message="translate(description)"
    >
      <template #mds-empty-state-icon>
        <mds-inline-message
          class="saved-feed__check-mark"
          variation="success"
          icon-only
        />
      </template>
      <template #mds-empty-state-actions>
        <p
          class="saved-feed__3rd-party-message"
          v-text="translate('scheduler.modal.3rdparty.description')"
        ></p>
        <mds-button
          class="saved-feed__close-button"
          variation="secondary"
          @click="onClick"
        >
          {{ translate('scheduler.modal.save.success.ok.button') }}
        </mds-button>
        <mds-button
          v-if="requestFromLens"
          class="saved-feed__redirect-button"
          variation="primary"
          :disabled="isRedirecting"
          :loading="isRedirecting"
          @click.prevent="redirectToDetails"
        >
          {{ translate('scheduler.modal.save.success.redirect.button') }}
        </mds-button>
      </template>
    </mds-empty-state>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { MdsButton } from '@mds/button'
import MdsEmptyState from '@mds/empty-state'
import MdsInlineMessage from '@mds/inline-message'
import LimitedAccessBanner from '@/components/limitedDistribution/LimitedAccessAlert.vue'
import MwcBaseHelpersMixin from '@morningstar/mcl-utilities/src/vue/mixins/mwc-base-mixin'
import defaultConfig from '@/components/schedulerModal/config/i18n'
import {
  SUCCESS_MODAL_MESSAGE,
  generateFeedDetailsMessage,
} from '@/components/schedulerModal/config/constants'
import { FULL_ACCESS } from '@/components/limitedDistribution/config/constants'

export default {
  name: 'SuccessfulResponseMessage',
  components: {
    MdsButton,
    MdsEmptyState,
    MdsInlineMessage,
    LimitedAccessBanner,
  },
  mixins: [MwcBaseHelpersMixin],
  props: {
    type: {
      type: String,
      default: 'feed',
    },
    attestationCheck: Boolean,
  },
  data() {
    return {
      defaultConfig,
      isRedirecting: false,
    }
  },
  computed: {
    ...mapState({
      cache: (state) => state.cache.cache,
    }),
    requestFromLens() {
      return this.cache.requestFromLens
    },
    records() {
      return this.cache.feeds ?? []
    },
    editedFeed() {
      return this.cache.editedFeed
    },
    isEditMode() {
      const { query = {} } = this.$route ?? {}
      const { view = '', feedId = '', scheduleId = '' } = query

      return !!feedId || !!scheduleId || view === 'schedule'
    },
    title() {
      return this.type === 'feed'
        ? 'scheduler.modal.saveFeed.success.title'
        : 'scheduler.modal.saveSchedule.success.title'
    },
    description() {
      if (this.type === 'feed') {
        return this.requestFromLens
          ? 'scheduler.modal.saveFeed.success.message.lens'
          : 'scheduler.modal.saveFeed.success.message'
      }
      return this.requestFromLens
        ? 'scheduler.modal.saveSchedule.success.message.lens'
        : 'scheduler.modal.saveSchedule.success.message'
    },

    limitedAccess() {
      // Check if the request is originating from Lens. We do not want to show the limited access message in that case
      // We only show this in Direct Classic Feed Manager
      if (this.requestFromLens) {
        return false
      }

      /* Until we retrieve the user settings from the endpoint, let's set those values
      as full access to hide the banner. Once we have the real values, the banner will
      listen to this computed changes. */
      const {
        data_unity_pr_access = FULL_ACCESS,
        data_unity_workspace_access = FULL_ACCESS,
      } = this.cache.userSettings

      return (
        data_unity_pr_access !== FULL_ACCESS ||
        data_unity_workspace_access !== FULL_ACCESS
      )
    },
    showAttestation() {
      const { approved_for_feed_distribution = false } = this.cache.userSettings
      if (approved_for_feed_distribution) {
        return !this.attestationCheck
      } else {
        return false
      }
    },
    feedsRow() {
      return this.cache?.userSettings?.data_unity_workspace_limit
    },
  },
  methods: {
    onClick() {
      if (this.requestFromLens) {
        window.chrome?.webview?.postMessage(SUCCESS_MODAL_MESSAGE)
        window.top.postMessage(SUCCESS_MODAL_MESSAGE, '*')
        return
      }
      this.$emit('close')
    },
    redirectToDetails() {
      this.isRedirecting = true
      const feed = this.isEditMode ? this.editedFeed : this.records.pop()
      const { query = {} } = this.$route ?? {}
      const { feedDetails = 'false', feedId = '' } = query
      const isFeedDetailsPage = JSON.parse(feedDetails)
      let action = isFeedDetailsPage
        ? 'ReloadFeedDetails'
        : 'RedirectFeedDetails'

      if (isFeedDetailsPage && !feedId) {
        action = 'RedirectFeedDetails'
      }
      const REDIRECT_FEED_DETAILS_MESSAGE = generateFeedDetailsMessage(
        action,
        feed
      )
      window.chrome?.webview?.postMessage(REDIRECT_FEED_DETAILS_MESSAGE)
      window.top.postMessage(REDIRECT_FEED_DETAILS_MESSAGE, '*')
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@mds/constants';
@import '@mds/typography';

.saved-feed {
  &__response-message {
    &::v-deep > [class^='mds-empty-state__icon'] {
      margin-bottom: 0;
    }
  }
  &__close-button {
    width: 99px;
    justify-content: center;
  }
  &__redirect-button {
    width: 160px;
    justify-content: center;
  }
  &__check-mark {
    &::v-deep > [class^='mds-icon'] {
      width: $mds-space-7-x;
    }
  }
  &__3rd-party-message {
    color: $mds-interactive-color-secondary-hover;
    margin-top: $mds-space-1-x;
  }
}
</style>
